<template>
    <div class="idle-carts-dashboard">

        <!-- Page Header -->
        <div class="d-flex justify-content-between align-items-center mb-4">
            <h3 class="mb-0"><font-awesome-icon icon="shopping-cart" class="mr-2 text-warning"/>Abandoned Cart Admin</h3>
            <b-button variant="primary" @click="refreshPage" :disabled="isLoading">
                <font-awesome-icon :icon="isLoading ? 'spinner' : 'sync'" :spin="isLoading" class="mr-1"/>
                Refresh
            </b-button>
        </div>

        <!-- MAIN TABS -->
        <b-tabs content-class="mt-3" pills>

            <!-- TAB 1: CARTS TABLE -->
            <b-tab title="Abandoned Carts" active>

                <!-- STATS ROW -->
                <b-row class="mb-4">
                        <!-- CART COUNT -->
                        <b-col md="4" lg>
                            <b-card class="stat-card text-center border-0 shadow-sm h-100 p-0" body-class="p-0">
                                <h4 class="mb-0 font-weight-bold">
                                    <span class="stat-icon text-warning mb-2 mr-2"><font-awesome-icon icon="shopping-cart"/></span>
                                    <font-awesome-icon v-if="isStatsLoading" icon="spinner" spin/>
                                    <template v-else>{{ stats.abandonedCount }}</template>
                                </h4>
                                <small class="text-muted">Abandoned Carts</small>
                            </b-card>
                        </b-col>
                        <!-- MISSING REVENUE -->
                        <b-col md="4" lg>
                            <b-card class="stat-card text-center border-0 shadow-sm h-100 p-0" body-class="p-0">

                                <h4 class="mb-0 font-weight-bold">
                                    <span class="stat-icon text-danger mb-2 mr-2"><!--<font-awesome-icon icon="dollar-sign" />--></span>
                                    <font-awesome-icon v-if="isStatsLoading" icon="spinner" spin/>
                                    <span v-else class="text-danger">{{ stats.revenueMissing }}</span>
                                </h4>
                                <small class="text-muted">Revenue Potential</small>
                            </b-card>
                        </b-col>

                        <!-- REVENUE RECOVERED -->
                        <b-col md="4" lg>
                            <b-card class="stat-card text-center border-0 shadow-sm h-100 p-0" body-class="p-0">
                                <h4 class="mb-0 font-weight-bold">
                                    <span class="stat-icon text-danger mb-2 mr-2"><!--<font-awesome-icon icon="dollar-sign" />--></span>
                                    <font-awesome-icon v-if="isStatsLoading" icon="spinner" spin/>
                                    <span v-else class="text-primary">{{ stats.revenueRecovered }}</span>
                                </h4>
                                <small class="text-muted">Revenue Recovered</small>
                            </b-card>
                        </b-col>

                        <!-- EMAIL OPEN RATE -->
                        <b-col md="6" lg>
                            <b-card class="stat-card text-center border-0 shadow-sm h-100 p-0" body-class="p-0">

                                <h4 class="mb-0 font-weight-bold">
                                    <span class="stat-icon text-info mb-2 mr-2"><font-awesome-icon icon="envelope-open-text" /></span>
                                    <font-awesome-icon v-if="isStatsLoading" icon="spinner" spin/>
                                    <template v-else>{{ stats.emailOpenRate }}%</template>
                                </h4>
                                <small class="text-muted">Email Open Rate</small>
                            </b-card>
                        </b-col>
                        <!-- RECOVERY RATE -->
                        <b-col md="4" lg>
                            <b-card class="stat-card text-center border-0 shadow-sm h-100 p-0" body-class="p-0">

                                <h4 class="mb-0 font-weight-bold">
                                    <span class="stat-icon text-success mb-2 mr-2"><font-awesome-icon icon="chart-line" /></span>
                                    <font-awesome-icon v-if="isStatsLoading" icon="spinner" spin/>
                                    <template v-else>{{ stats.recoveryRate }}%</template>
                                </h4>
                                <small class="text-muted">Recovery Rate</small>
                            </b-card>
                        </b-col>
                        <!-- TOTAL CONVERSIONS -->
                        <!--            <b-col md="6" lg>-->
                        <!--                <b-card class="stat-card text-center border-0 shadow-sm h-100">-->
                        <!--                    <div class="stat-icon text-purple mb-2"><font-awesome-icon icon="mouse-pointer" size="2x"/></div>-->
                        <!--                    <h2 class="mb-0 font-weight-bold">{{ stats.conversionAfterEmail }}%</h2>-->
                        <!--                    <small class="text-muted">Conversion After Email</small>-->
                        <!--                </b-card>-->
                        <!--            </b-col>-->
                    </b-row>

                <!-- Filters -->
                <b-card class="mb-2 border-0 shadow-sm">
                    <!-- Date Range -->
                    <b-row class="mb-1">
                        <b-col sm="4" class="mb-2">
                            <b-form-group class="mb-0" label="Preset" label-size="sm">
                                <b-form-select size="sm" v-model="datePreset" :options="datePresetOptions" @change="applyDatePreset"/>
                            </b-form-group>
                        </b-col>
                        <b-col sm="4" class="mb-2">
                            <b-form-group class="mb-0" label="From" label-size="sm">
                                <b-form-input size="sm" type="date" v-model="filters.dateFrom" @change="changedDates"/>
                            </b-form-group>
                        </b-col>
                        <b-col sm="4" class="mb-2">
                            <b-form-group class="mb-0" label="To" label-size="sm">
                                <b-form-input size="sm" type="date" v-model="filters.dateTo" @change="changedDates"/>
                            </b-form-group>
                        </b-col>
                    </b-row>
                    <!-- SEARCH -->
                    <b-row>
                        <b-col md="6">
                            <b-form-group label-size="sm" class="mb-0">
                                <b-input-group size="sm">
                                    <b-input-group-prepend is-text><font-awesome-icon icon="search"/></b-input-group-prepend>
                                    <b-form-input v-model="filters.search" placeholder="Email, name, or cart ID…" debounce="700" name="search" />
                                </b-input-group>
                            </b-form-group>
                        </b-col>
                    </b-row>
                    <!-- Dropdowns -->
                    <b-row class="mt-2">
                        <!-- STATE -->
                        <b-col md="3">
                            <b-form-group label-size="sm" class="mb-0">
                                <b-form-select size="sm" v-model="filters.state" :options="stateOptions"/>
                            </b-form-group>
                        </b-col>
                        <!-- USER -->
                        <b-col md="3">
                            <b-form-group label-size="sm" class="mb-0">
                                <b-form-select size="sm" v-model="filters.user" :options="userOptions" />
                            </b-form-group>
                        </b-col>
                        <!-- EMAIL STATUS -->
                        <b-col md="3">
                            <b-form-group label-size="sm" class="mb-0">
                                <b-form-select size="sm" v-model="filters.emailStatus" :options="emailStatusOptions" />
                            </b-form-group>
                        </b-col>
                        <!-- CART TYPE (HOMESCHOOL) -->
                        <b-col md="3" class="mt-2 mt-md-0">
                            <b-form-group label-size="sm" class="mb-0">
                                <b-form-select size="sm" v-model="filters.homeschool" :options="homeschoolOptions" />
                            </b-form-group>
                        </b-col>
                        <!-- RESET TO DEFAULTS -->
                        <b-col md="2" class="d-flex align-items-end">
                            <b-button size="sm" variant="outline-secondary" @click="resetFilters" block>
                                <font-awesome-icon icon="times" class="mr-1"/>Clear
                            </b-button>
                        </b-col>
                    </b-row>
                </b-card>

                <!-- Table -->
                <b-card class="border-0 shadow-sm">
                    <b-pagination
                        v-model="currentPage"
                        :total-rows="totalRows"
                        :per-page="perPage"
                        @input="getIdleCarts"
                        align="right"
                        size="sm"
                        class="mt-0 mb-0"
                    />
                    <b-table
                        :items="filteredCarts"
                        :fields="cartFields"
                        striped
                        hover
                        responsive
                        show-empty
                        empty-text="No carts found."
                        :busy="isLoading"

                        selectable
                        select-mode="single"
                        @row-selected="onRowSelected"
                    >
                        <template #table-busy>
                            <div class="text-center my-4">
                                <b-spinner variant="primary"/><br/>
                                <span class="text-muted mt-2">Loading carts…</span>
                            </div>
                        </template>

                        <!-- USER -->
                        <template #cell(user)="data">
                            <span v-if="data.item.user_id">
                                <b-button size="sm" variant="outline-secondary" :id="`user-popover-${data.item.id}`">
                                    <font-awesome-icon icon="user"/>
                                </b-button>
                                <b-popover :target="`user-popover-${data.item.id}`" triggers="hover" placement="top">
                                    <template #title>User #{{ data.item.user_id }}</template>
                                    <div><strong>Username:</strong> {{ data.item.user && data.item.user.username }}</div>
                                    <div><strong>Email:</strong> {{ data.item.user && data.item.user.username }}</div>
                                </b-popover>
                            </span>
                            <b-badge v-else variant="secondary">Guest</b-badge>
                        </template>

                        <!-- CART TYPE -->
                        <template #cell(homeschool)="data">
                            <b-badge v-if="data.item.homeschool == '1'" variant="info" v-b-tooltip.hover title="Homeschool cart">
                                <font-awesome-icon :icon="['fas', 'home']" class="mr-1"/>Homeschool
                            </b-badge>
                            <b-badge v-else variant="secondary" v-b-tooltip.hover title="School / District cart">
                                <font-awesome-icon icon="school" class="mr-1"/>School
                            </b-badge>
                        </template>

                        <!-- CART DATA -->
                        <template #row-details="data">
                            <b-row>
                                <b-col>
                                    Multiyear: {{ data.item.data[0].multiyear }}<br/>
                                    Teachers: {{ data.item.data[0].teachers }}
                                </b-col>
                                <b-col>
                                    <b-table
                                        :items="data.item.data"
                                        :fields="['sku', {key:'quantity', label: 'Qty'}]"
                                        small
                                        class="mb-0"
                                    />
                                </b-col>
                            </b-row>
                        </template>

                        <template #cell(subtotal)="data">
                            <strong>${{ Number(data.item.subtotal).toFixed(2) }}</strong>
                        </template>

                        <template #cell(last_sent_at)="data">
                            <b-badge :variant="data.item.last_sent_at ? 'success' : 'secondary'">
                                {{ data.item.last_sent_at ? 'Sent' : 'Pending' }}
                            </b-badge>
                        </template>

                        <template #cell(custom_status)="data">
                            <b-badge v-if="data.item.last_converted_at" variant="primary">Converted</b-badge>
                            <b-badge v-else-if="data.item.last_clicked_at" variant="success">Clicked</b-badge>
                            <b-badge v-else-if="data.item.last_opened_at" variant="info">Opened</b-badge>
                            <b-badge v-else variant="light">--</b-badge>


                        </template>

                        <template #cell(updated_at)="data">
                            {{ data.item.updated_at | moment("MMM D, YYYY h:mm A") }}
                        </template>

                        <template #cell(actions)="data">
                            <b-button-group size="sm">
                                <b-button variant="outline-primary" @click="openPreview(data.item)" v-b-tooltip.hover title="Preview Email">
                                    <font-awesome-icon icon="eye"/>
                                </b-button>
                                <b-button :disabled="true" variant="outline-success" @click="sendEmailToCart(data.item)" v-b-tooltip.hover title="Send Recovery Email">
                                    <font-awesome-icon icon="paper-plane"/>
                                </b-button>
                            </b-button-group>
                        </template>
                    </b-table>


                </b-card>
            </b-tab>


            <!-- TAB 2: EMAIL EDITOR -->
            <b-tab title="Email Template">
                <b-row>
                    <b-col lg="8">
                        <b-card class="border-0 shadow-sm mb-3">
                            <b-form-group class="mb-3">
                                <template #label><h5>Subject Line</h5></template>
                                <b-form-input v-model="emailSubject" placeholder="You left something behind!" style="max-width:480px;"/>
                                <b-form-text>Available variables: <code>[name]</code>, <code>[cart_total]</code></b-form-text>
                            </b-form-group>

                            <div class="d-flex justify-content-between align-items-center mb-3">
                                <h5 class="mb-0">HTML Email Editor</h5>
                            </div>

                            <!-- Mode toggle -->
                            <b-button-group size="sm" class="mb-2">
                                <b-button :variant="editorMode === 'visual' ? 'primary' : 'outline-secondary'" @click="editorMode = 'visual'">
                                    <font-awesome-icon icon="eye" class="mr-1"/>Visual
                                </b-button>
                                <b-button :variant="editorMode === 'html' ? 'primary' : 'outline-secondary'" @click="editorMode = 'html'">
                                    <font-awesome-icon icon="code" class="mr-1"/>HTML
                                </b-button>
                            </b-button-group>

                            <div v-if="editorMode === 'visual'" class="border rounded p-2" style="min-height: 600px;">
                                <div class="ql-toolbar-wrapper">
                                    <!-- Minimal rich text toolbar -->
                                    <b-button-group size="sm" class="mb-2 flex-wrap">
                                        <b-button variant="outline-secondary" @click="execCmd('bold')" v-b-tooltip.hover title="Bold"><strong>B</strong></b-button>
                                        <b-button variant="outline-secondary" @click="execCmd('italic')" v-b-tooltip.hover title="Italic"><em>I</em></b-button>
                                        <b-button variant="outline-secondary" @click="execCmd('underline')" v-b-tooltip.hover title="Underline"><u>U</u></b-button>
                                        <b-button variant="outline-secondary" @click="execCmd('insertUnorderedList')" v-b-tooltip.hover title="Bullet list"><font-awesome-icon icon="list-ul"/></b-button>
                                        <b-button variant="outline-secondary" @click="execCmd('justifyLeft')" v-b-tooltip.hover title="Align left"><font-awesome-icon icon="align-left"/></b-button>
                                        <b-button variant="outline-secondary" @click="execCmd('justifyCenter')" v-b-tooltip.hover title="Align center"><font-awesome-icon icon="align-center"/></b-button>
                                        <b-button variant="outline-secondary" @click="execCmd('justifyRight')" v-b-tooltip.hover title="Align right"><font-awesome-icon icon="align-right"/></b-button>
                                        <b-button variant="outline-secondary" @click="promptLink" v-b-tooltip.hover title="Insert link"><font-awesome-icon icon="link"/></b-button>
                                    </b-button-group>
                                </div>
                                <div
                                    ref="visualEditor"
                                    contenteditable="true"
                                    class="visual-editor p-2"
                                    style="min-height:560px; outline:none;"
                                    v-html="emailTemplate"
                                />
                            </div>

                            <div v-else>
                                <prism-editor
                                    v-model="emailTemplate"
                                    :highlight="highlightHtml"
                                    line-numbers
                                    class="email-code-editor border rounded"
                                    style="min-height: 600px; font-size: 13px;"
                                />
                            </div>
                        </b-card>
                    </b-col>

                    <b-col lg="4">
                        <b-card class="border-0 shadow-sm mb-1">
                            <h5 class="mb-3">Actions</h5>

                            <!-- Send Test Email -->
                            <b-form-group label="Send Test Email To">
                                <b-input-group size="sm">
                                    <b-form-input v-model="testEmailAddress" type="email" placeholder="test@example.com"/>
                                    <b-input-group-append>
                                        <b-button variant="outline-primary" @click="sendTestEmail" :disabled="isSendingTest">
                                            <font-awesome-icon :icon="isSendingTest ? 'spinner' : 'paper-plane'" :spin="isSendingTest"/>
                                        </b-button>
                                    </b-input-group-append>
                                </b-input-group>
                            </b-form-group>

                            <hr/>

                            <!-- Preview with real data -->
                            <b-form-group label="Preview With Cart Data">
                                <b-form-select size="sm" v-model="previewCartId" :options="cartSelectOptions" class="mb-2"/>
                                <b-button size="sm" variant="outline-info" block @click="openPreviewModal">
                                    <font-awesome-icon icon="eye" class="mr-1"/>Preview Email
                                </b-button>
                            </b-form-group>

                            <hr/>
                            <b-button variant="success" block @click="saveTemplate" :disabled="isSavingTemplate">
                                <font-awesome-icon :icon="isSavingTemplate ? 'spinner' : 'save'" :spin="isSavingTemplate" class="mr-1"/>
                                Save Template
                            </b-button>
                        </b-card>

                        <b-card class="border-0 shadow-sm">
                            <h6>Available Parameters</h6>
                            <b-form-text class="mb-2">Click a parameter badge to copy it. Paste into the editor below.</b-form-text>
                            <b-list-group flush>
                                <b-list-group-item v-for="param in templateParams" :key="param.key" class="px-0 py-1 d-flex justify-content-between align-items-center border-0">
                                    <b-badge variant="light" class="mr-1 border"
                                         style="cursor:pointer; font-size:.8rem;"
                                         @click="insertTemplateParam(param.key)"
                                         v-b-tooltip.hover :title="`Click to copy: [${param.key}]`"
                                    >
                                        <font-awesome-icon icon="code" class="mr-1"/>{{ param.label }}
                                    </b-badge>
                                </b-list-group-item>
                            </b-list-group>
                        </b-card>
                        <b-button variant="danger" block @click="resetEmailTemplate" :disabled="isSavingTemplate">
                            Reset Template
                        </b-button>
                    </b-col>
                </b-row>
            </b-tab>

            <!-- TAB 3: SETTINGS -->
            <b-tab title="Settings">
                <b-card class="border-0 shadow-sm">
                    <h5 class="mb-3">Recovery Email Parameters</h5>
                    <b-form @submit.prevent="saveSettings">
                        <b-form-group
                            label="Enable Automatic Recovery Emails"
                        >
                            <b-form-checkbox v-model="settings.autoSendEnabled" switch size="lg">
                                {{ settings.autoSendEnabled ? 'Enabled' : 'Disabled' }}
                            </b-form-checkbox>
                        </b-form-group>
                        <b-form-group
                            label="Enable Recovery Emails for Homeschool Orders"
                        >
                            <b-form-checkbox v-model="settings.autoSendHomeschoolEnabled" switch size="lg">
                                {{ settings.autoSendHomeschoolEnabled ? 'Enabled' : 'Disabled' }}
                            </b-form-checkbox>
                        </b-form-group>

                        <b-form-group
                            label="Send Delay (hours)"
                        >
                            <b-input-group append="hours" style="max-width: 220px;">
                                <b-form-input
                                    v-model.number="settings.sendDelayHours"
                                    type="number"
                                    min="1"
                                    max="168"
                                />
                            </b-input-group>
                        </b-form-group>

                        <b-form-group label="Reply-To Email">
                            <b-form-input v-model="settings.replyTo" type="email" placeholder="support@example.com" style="max-width:320px;"/>
                        </b-form-group>

                        <b-button type="submit" variant="primary" :disabled="isSavingSettings">
                            <font-awesome-icon :icon="isSavingSettings ? 'spinner' : 'save'" :spin="isSavingSettings" class="mr-1"/>
                            Save Settings
                        </b-button>
                    </b-form>
                </b-card>
            </b-tab>

            <!-- TAB 4: SENT REMINDERS -->
            <b-tab title="Sent Reminders">
                <b-pagination :total-rows="reminderPagination.totalRows" @input="getReminders" size="sm" :per-page="25"/>
                <b-table
                    :items="sentReminders"
                    :fields="reminderFields"
                    :busy="sentReminders.length == 0"
                    striped
                    hover
                    responsive
                    small>
                    <template #cell(sent_at)="data">
                        <span v-if="data.item.sent_at">{{ data.item.sent_at | moment("MMM D, YYYY h:mm A") }}</span>
                        <span v-else class="text-muted">--</span>
                    </template>

                    <template #cell(latest_activity)="data">
                        <template v-if="latestReminderActivity(data.item)">
                            <b-badge :variant="latestReminderActivity(data.item).variant">{{ latestReminderActivity(data.item).label }}</b-badge>
                            <small class="text-muted ml-1">{{ latestReminderActivity(data.item).at | moment("MMM D, YYYY h:mm A") }}</small>
                        </template>
                        <span v-else class="text-muted">--</span>
                    </template>

                    <template #cell(opened_at)="data">
                        <b-badge :variant="data.item.opened_at ? 'success' : 'light'" v-b-popover.hover.top="data.item.opened_at">
                            <font-awesome-icon v-if="data.item.opened_at" :icon="['fas', 'check']" />
                            <span v-else>--</span>
                        </b-badge>
                    </template>

                    <template #cell(clicked_at)="data">
                        <b-badge :variant="data.item.clicked_at ? 'success' : 'light'" v-b-popover.hover.top="data.item.clicked_at">
                            <font-awesome-icon v-if="data.item.clicked_at" :icon="['fas', 'check']" />
                            <span v-else>--</span>
                        </b-badge>
                    </template>

                    <template #cell(converted_at)="data">
                        <b-badge :variant="data.item.converted_at ? 'success' : 'light'" v-b-popover.hover.top="data.item.converted_at">
                            <font-awesome-icon v-if="data.item.converted_at" :icon="['fas', 'check']" />
                            <span v-else>--</span>
                        </b-badge>
                    </template>
                </b-table>
            </b-tab>
        </b-tabs>

        <!-- ─── EMAIL PREVIEW MODAL ─── -->
        <b-modal id="email-preview-modal" size="xl" title="Email Preview" hide-footer scrollable>
            <div v-if="previewCart" class="mb-3">
                <b-alert show variant="info" class="py-2">
                    <small>Previewing with data from Cart #{{ previewCart.id }} — {{ previewCart.user && previewCart.user.username }}</small>
                </b-alert>
            </div>
            <div class="border rounded p-3 bg-white" v-html="renderedPreview" style="min-height:300px;"/>
        </b-modal>

    </div>
</template>

<script>
import { PrismEditor } from 'vue-prism-editor';
import 'vue-prism-editor/dist/prismeditor.min.css';
import { highlight, languages } from 'prismjs/components/prism-core';
import 'prismjs/components/prism-markup';
import 'prismjs/themes/prism-tomorrow.css';

const today = new Date();
const yyyy = today.getFullYear();
const mm = String(today.getMonth() + 1).padStart(2, '0');
const dd = String(today.getDate()).padStart(2, '0');
const todayStr = `${yyyy}-${mm}-${dd}`;
const yearStart = `${yyyy}-01-01`;
let perPage = parseInt(localStorage.getItem("carts_per_page") || '25');
const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
});

export default {
    name: "IdleCarts",
    components: { PrismEditor },

    data: () => ({
        isLoading: false,
        isStatsLoading: false,
        isSavingSettings: false,
        isSavingTemplate: false,
        isSendingTest: false,

        carts: [],
        selectedCart: null,


        // Filters
        filters: {
            dateFrom: yearStart,
            dateTo: todayStr,
            search: '',
            state: null,
            emailStatus: null,
            user: 'user',
            homeschool: null,
        },
        datePreset: 'this_year',

        // Pagination
        perPage: 15,
        currentPage: 0,
        totalRows: 1,

        // Stats (populated from API or derived)
        stats: {
            revenueMissing: 0,
            abandonedCount: 0,
            recoveryRate: 0,
            revenueRecovered: 0,
            emailOpenRate: 0,
            conversionAfterEmail: 0,
        },

        // Settings
        settings: {
            autoSendHomeschoolEnabled: false,
            autoSendEnabled: false,
            sendDelayHours: 24,
            replyTo: '',
        },

        // Email template
        editorMode: 'visual',
        emailTemplate: '',
        emailSubject: 'You left something in your cart, [name]!',
        testEmailAddress: '',

        // Preview
        previewCartId: null,
        previewCart: null,

        templateParams: [
            { key: 'name',    label: 'Real Name' },
            { key: 'username',    label: 'Username' },
            { key: 'email',         label: 'Email' },
            { key: 'cart_total',    label: 'Cart Total' },
            { key: 'cart_items',    label: 'Cart Items (HTML rows)' },
            { key: 'recovery_link', label: 'Recovery Link' },
            { key: 'state',         label: 'State' },
        ],

        // Sent Reminders
        sentReminders: [],
        reminderPagination: {
            totalPages: 0,
        },
    }),

    watch: {
        'filters.search'() {
            this.getIdleCarts();
            this.fetchStats();
        },
        'filters.dateFrom'() {
            this.getIdleCarts();
            this.fetchStats();
        },
        'filters.dateTo'() {
            this.getIdleCarts();
            this.fetchStats();
        },
         'filters.state'() {
             this.getIdleCarts();
             this.fetchStats();
        },
         'filters.emailStatus'() {
             this.getIdleCarts();
             this.fetchStats();
        },
         'filters.user'() {
             this.getIdleCarts();
             this.fetchStats();
        },
         'filters.homeschool'() {
             this.getIdleCarts();
             this.fetchStats();
        }
    },
    computed: {

        datePresetOptions() {
            const y = new Date().getFullYear();
            let uniqueYears = _.uniq(this.carts.map(cart => new Date(cart.updated_at).getFullYear()));
            let ranges = [
                { value: 'all',        text: 'All Time' },
                { value: 'today',      text: 'Today' },
                { value: 'this_week',  text: 'This Week' },
                { value: 'this_month', text: 'This Month' },
                { value: 'last_month', text: 'Last Month' },
                { value: 'this_year',  text: `${y} (YTD)` },
                { value: 'custom',     text: 'Custom Range' },
            ];
            // for (let year of uniqueYears) {
            //     if (year !== y)
            //         ranges.push({ value: `${year}`, text: year });
            // }
            return ranges;
        },
        cartFields() {
            return [
                { key: 'id', label: 'ID', sortable: true },
                { key: 'user', label: 'User' },
                { key: 'homeschool', label: 'Type', sortable: true },
                { key: 'state', label: 'State', sortable: true },
                // { key: 'data', label: 'Products' },
                { key: 'subtotal', label: 'Subtotal', sortable: true },
                { key: 'last_sent_at', label: 'Email', sortable: true },
                { key: 'custom_status', label: 'Status', sortable: true },
                { key: 'updated_at', label: 'Last Change', sortable: true },
                { key: 'actions', label: '' },
            ];
        },
        reminderFields() {
            return [
                { key: 'cart_id', label: 'Cart ID', sortable: true },
                // { key: 'email_template_id', label: 'Template ID', sortable: true },
                { key: 'email_to', label: 'Email', sortable: true },
                { key: 'sent_at', label: 'Emailed', sortable: true },
                { key: 'opened_at', label: 'Opened', sortable: true },
                { key: 'clicked_at', label: 'Clicked', sortable: true },
                { key: 'converted_at', label: 'Converted', sortable: true },
                {
                    key: 'latest_activity',
                    label: 'Latest Activity',
                    sortable: true,
                    formatter: (value, key, item) => {
                        const latest = this.latestReminderActivity(item);
                        return latest ? latest.at : '';
                    },
                    sortByFormatted: true,
                },
            ];
        },
        userOptions() {
            return [
                { value : null, text: 'All Carts' },
                { value : 'user', text: 'With Users' },
                { value : 'no_user', text: 'Without Users' },
            ];
        },

        homeschoolOptions() {
            return [
                { value: null, text: 'All Types' },
                { value: '1', text: 'Homeschool' },
                { value: '0', text: 'School / District' },
            ];
        },

        stateOptions() {
            const states = [...new Set(this.carts.map(c => c.state).filter(Boolean))].sort();
            return [{ value: null, text: 'All States' }, ...states.map(s => ({ value: s, text: s }))];
        },

        emailStatusOptions() {
            return [
                { value: null, text: 'All Statuses' },
                { value: 'sent', text: 'Email Sent' },
                { value: 'pending', text: 'Not Sent' },
                { value: 'opened', text: 'Opened' },
            ];
        },

        filteredCarts() {
            return this.carts;
        },

        cartSelectOptions() {
            const opts = this.carts.map(c => ({
                value: c.id,
                text: `Cart #${c.id} — ${(c.user && c.user.username) || 'Guest'} ($${Number(c.subtotal || 0).toFixed(2)})`,
            }));
            return [{ value: null, text: 'Select a cart…' }, ...opts];
        },

        renderedPreview() {
            if (!this.previewCart) return this.emailTemplate;
            const c = this.previewCart;
            const user = c.user || {};
            const items = (c.data || []).map(i =>
                `<tr><td>${i.title || i.sku}</td><td align="right">${i.quantity}</td></tr>`
            ).join('');
            const itemTable = `
                <table width="100%" cellpadding="8" cellspacing="0" style="border-collapse:collapse;">
                    <tr style="background:#f5f5f5;">
                        <th align="left">Product</th>
                        <th align="right">Qty</th>
                    </tr>
                    ${items}
                </table>`;
            return this.emailTemplate
                .replace(/\[name\]/g, user.name || user.username || 'Valued Customer')
                .replace(/\[username\]/g, user.name || user.username || 'Valued Customer')
                .replace(/\[email\]/g, user.username || '')
                .replace(/\[cart_total\]/g, `$${Number(c.subtotal || 0).toFixed(2)}`)
                .replace(/\[cart_items\]/g, itemTable)
                .replace(/\[recovery_link\]/g, `${window.location.origin}/cart/recover/${c.id}`)
                .replace(/\[state\]/g, c.state || '');
        },
    },

    created() {
        this.getIdleCarts();
        this.loadSettings();
        this.loadTemplate();
        this.fetchStats();
    },
    mounted() {
    },

    methods: {
        // Most recent of opened/clicked/converted; ties go to the later funnel step
        latestReminderActivity(reminder) {
            const events = [
                { at: reminder.opened_at, label: 'Opened', variant: 'info' },
                { at: reminder.clicked_at, label: 'Clicked', variant: 'success' },
                { at: reminder.converted_at, label: 'Converted', variant: 'primary' },
            ].filter(e => e.at);
            if (!events.length) return null;
            return events.reduce((latest, e) => (!this.$moment(e.at).isBefore(latest.at) ? e : latest));
        },
        refreshPage() {
            this.getIdleCarts();
            this.getReminders();
            this.fetchStats();
        },
        async getIdleCarts() {
            this.isLoading = true;
            try {
                const response = await this.axios.get('/cart/list', { params: { page: this.currentPage || 0, filters: this.filters } });
                this.carts = response.data.data;
                this.totalRows = response.data.meta.total;
            } catch (e) {
                this.$bvToast.toast('Failed to load carts.', { title: 'Error', variant: 'danger', solid: true });
            } finally {
                this.isLoading = false;
            }
        },
        async fetchStats() {
            this.isStatsLoading = true;
            try {
                let response = await this.axios.get('/cart/stats', {params: {filters: JSON.stringify(this.filters)}});
                this.stats.abandonedCount = response.data.abandonedCount;
                this.stats.recoveryRate = response.data.recoveryRate;
                this.stats.revenueRecovered = formatter.format(response.data.revenueRecovered);
                this.stats.revenueMissing = formatter.format(response.data.revenueMissing);
                this.stats.emailOpenRate = response.data.emailOpenRate;
                this.stats.conversionAfterEmail = response.data.conversionAfterEmail;
            } finally {
                this.isStatsLoading = false;
            }
        },
        onRowSelected(rows) {
            if (this.selectedCart)
                this.selectedCart._showDetails = false;

            this.selectedCart = rows[0];
            console.log(this.selectedCart);
            this.selectedCart._showDetails = (!this.selectedCart._showDetails);
        },


        resetFilters() {
            const today = new Date();
            const pad = n => String(n).padStart(2, '0');
            const y = today.getFullYear();

            this.filters = {
                search: '',
                state: null,
                user: 'user',
                dateFrom: `${y}-01-01`,
                dateTo: `${y}-${pad(today.getMonth()+1)}-${pad(today.getDate())}`,
                emailStatus: null,
                homeschool: null
            };
            this.getIdleCarts();
        },

        async saveSettings() {
            this.isSavingSettings = true;
            try {
                await this.axios.post('/cart/recovery/settings', this.settings);
                this.$bvToast.toast('Settings saved successfully.', { title: 'Success', variant: 'success', solid: true });
            } catch {
                this.$bvToast.toast('Failed to save settings.', { title: 'Error', variant: 'danger', solid: true });
            } finally {
                this.isSavingSettings = false;
            }
        },

        async loadSettings() {
            try {
                const res = await this.axios.get('/cart/recovery/settings');
                if (res.data) {
                    this.settings = {
                        autoSendHomeschoolEnabled: res.data.autoSendHomeschoolEnabled === "1" || false,
                        autoSendEnabled: res.data.autoSendEnabled === "1" || false,
                        sendDelayHours: res.data.sendDelayHours || 24,
                        replyTo: res.data.replyTo || '',
                    };
                }
                console.log(this.settings);
            } catch { /* use defaults */ }
        },

        async saveTemplate() {
            // Save email template variable
            this.emailTemplate = this.$refs.visualEditor.innerHTML;

            this.isSavingTemplate = true;
            try {
                await this.axios.post('/cart/recovery/template', { html: this.emailTemplate, subject: this.emailSubject });
                this.$bvToast.toast('Template saved.', { title: 'Success', variant: 'success', solid: true });
            } catch {
                this.$bvToast.toast('Failed to save template.', { title: 'Error', variant: 'danger', solid: true });
            } finally {
                this.isSavingTemplate = false;
            }
        },

        async loadTemplate() {
            try {
                const res = await this.axios.get('/cart/recovery/template');
                if (res.data && res.data.html) this.emailTemplate = res.data.html;
                if (res.data && res.data.subject) this.emailSubject = res.data.subject;
            } catch { /* use default template */ }
        },

        async sendTestEmail() {
            if (!this.testEmailAddress) {
                this.$bvToast.toast('Please enter an email address.', { title: 'Warning', variant: 'warning', solid: true });
                return;
            }
            if (!this.testEmailAddress.match('@studiesweekly.com')) {
                this.$bvToast.toast('Test emails must be sent to @studiesweekly.com addresses.', { title: 'Warning', variant: 'warning', solid: true });
                return;
            }

            this.isSendingTest = true;
            try {
                await this.axios.post('/cart/recovery/testEmail', {
                    cart_id: this.previewCartId,
                    email: this.testEmailAddress,
                    html: this.emailTemplate,
                    subject: this.settings.subject,
                });
                this.$bvToast.toast(`Test email sent to ${this.testEmailAddress}.`, { title: 'Sent!', variant: 'success', solid: true });
            } catch {
                this.$bvToast.toast('Failed to send test email.', { title: 'Error', variant: 'danger', solid: true });
            } finally {
                this.isSendingTest = false;
            }
        },

        async sendEmailToCart(cart) {
            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Send recovery email to ${(cart.user && cart.user.username) || 'guest'}?`,
                { title: 'Confirm Send', okVariant: 'success', okTitle: 'Send', cancelTitle: 'Cancel' }
            );
            if (!confirmed) return;
            try {
                await this.axios.post(`/cart/${cart.id}/send-recovery-email`);
                this.$bvToast.toast('Recovery email sent.', { title: 'Sent!', variant: 'success', solid: true });
                await this.getIdleCarts();
            } catch {
                this.$bvToast.toast('Failed to send email.', { title: 'Error', variant: 'danger', solid: true });
            }
        },
        async getReminders(pageNum = 0) {
            this.sentReminders = [];
            try {
                const response = await this.axios.get('/cart/recovery/reminders', { params: { page: pageNum || 0 } });
                this.reminderPagination.totalPages = response.data.last_page || 1;
                this.reminderPagination.totalRows = response.data.total || 1;
                this.sentReminders = response.data.data;
            } catch (e) {
                this.$bvToast.toast('Failed to load reminders.', { title: 'Error', variant: 'danger', solid: true });
                console.log(e);
            }
        },

        openPreview(cart) {
            this.previewCart = cart;
            this.$bvModal.show('email-preview-modal');
        },

        openPreviewModal() {
            this.previewCart = this.carts.find(c => c.id === this.previewCartId) || null;
            this.$bvModal.show('email-preview-modal');
        },

        insertTemplateParam(key) {
            const tag = `[${key}]`;
            navigator.clipboard.writeText(tag).then(() => {
                this.$bvToast.toast(`Copied ${tag} to clipboard.`, { title: 'Copied', variant: 'info', solid: true, autoHideDelay: 1500 });
            });
        },

        execCmd(cmd) {
            document.execCommand(cmd, false, null);
            this.$refs.visualEditor && this.$refs.visualEditor.focus();
        },

        promptLink() {
            const url = window.prompt('Enter URL:', 'https://');
            if (url) document.execCommand('createLink', false, url);
        },

        highlightHtml(code) {
            return highlight(code, languages.markup);
        },
        resetEmailTemplate() {
            this.emailTemplate = `<div style="font-family:Arial,sans-serif;max-width:600px;margin:auto;">
                <h2 style="color:#333;">Hey [name], you left something behind!</h2>
                <p>You had items in your cart totaling <strong>[cart_total]</strong>. Come back and complete your order!</p>
                [cart_items]
                <br/>
                <a href="[recovery_link]" style="background:#0056b3;color:#fff;padding:12px 24px;border-radius:4px;text-decoration:none;display:inline-block;">
                    Complete My Order
                </a>
                <p style="color:#999;font-size:12px;margin-top:24px;">You're receiving this because you started a checkout on our site.</p>
            </div>`;
        },
        applyDatePreset(preset) {
            const today = new Date();
            const pad = n => String(n).padStart(2, '0');
            const fmt = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
            const y = today.getFullYear();
            const m = today.getMonth();

            const ranges = {
                all:        [null, null],
                today:      [fmt(today), fmt(today)],
                this_week:  [fmt(new Date(today.setDate(today.getDate() - today.getDay()))), fmt(new Date())],
                this_month: [`${y}-${pad(m+1)}-01`, fmt(new Date())],
                last_month: [
                    fmt(new Date(y, m - 1, 1)),
                    fmt(new Date(y, m, 0)),
                ],
                this_year:  [`${y}-01-01`, fmt(new Date())],
                last_year:  [`${y-1}-01-01`, `${y-1}-12-31`],
            };

            if (preset === 'custom' || !ranges[preset]) return;

            const [from, to] = ranges[preset];
            this.filters.dateFrom = from || '';
            this.filters.dateTo   = to   || '';

        },
        changedDates() {
            this.datePreset = 'custom';
        }
    },
};
</script>

<style scoped lang="scss">
.idle-carts-dashboard {
    padding: 1.5rem;
}

.stat-card {
    transition: transform .15s;
    &:hover { transform: translateY(-2px); }
}

.stat-icon { opacity: .85; }

.text-purple { color: #6f42c1 !important; }

.visual-editor {
    border: 1px solid #dee2e6;
    border-radius: .25rem;
    min-height: 360px;
    background: #fff;
}

.email-code-editor {
    background: #2d2d2d;
    color: #ccc;
    font-family: 'Fira Code', monospace;
    border-radius: .25rem;
    padding: .5rem;
}
</style>